import { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Label }  from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { DraggableShell } from '@/components/ui/draggable-shell'
import { Download, Eye, Trash2, Plus } from 'lucide-react'
import { createGeneralApi } from '../tbsGeneralApi'
import { cn, toCssLength, toStyleObject, generateId } from '@/lib/utils'

function formatSize(bytes) {
  if (bytes < 1024)    return `${bytes} o`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} Ko`
  return `${(bytes / 1048576).toFixed(1)} Mo`
}

// Types MIME devinés depuis l'extension quand `type` est absent/vide (fileWrapper serveur créé sans
// `type` explicite, ou navigateur ne le détectant pas pour certaines extensions) — sans ça, l'icône
// de liste et la prévisualisation (image/PDF/texte inline) ne fonctionnent jamais pour ces fichiers,
// quel que soit leur contenu réel. Extensions bureautiques/archives incluses même si non
// prévisualisables : servent uniquement à l'icône (voir iconClassFor ci-dessous).
const EXT_TO_MIME = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  webp: 'image/webp', svg: 'image/svg+xml', bmp: 'image/bmp',
  pdf: 'application/pdf',
  txt: 'text/plain', log: 'text/plain', csv: 'text/csv', md: 'text/markdown', json: 'application/json',
  xml: 'application/xml',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  zip: 'application/zip', rar: 'application/vnd.rar', '7z': 'application/x-7z-compressed',
  mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg',
  mp4: 'video/mp4', webm: 'video/webm', avi: 'video/x-msvideo',
}
function inferMimeType(name, type) {
  if (type) return type
  const ext = name?.includes('.') ? name.split('.').pop().toLowerCase() : ''
  return EXT_TO_MIME[ext] ?? ''
}
const isTextType = type => !!type && (type.startsWith('text/') || type === 'application/json')

// Icône de liste par type MIME (Font Awesome — icônes "contenu", cohérent avec le reste de l'app ;
// les boutons d'action Visualiser/Télécharger/Supprimer restent en Lucide, icônes "système").
const WORD_TYPES = new Set(['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'])
const EXCEL_TYPES = new Set(['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'])
const PPT_TYPES = new Set(['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'])
const ARCHIVE_TYPES = new Set(['application/zip', 'application/vnd.rar', 'application/x-7z-compressed', 'application/x-tar', 'application/gzip'])
function iconClassFor(type) {
  if (!type) return 'fa-solid fa-file'
  if (type.startsWith('image/')) return 'fa-solid fa-file-image'
  if (type === 'application/pdf') return 'fa-solid fa-file-pdf'
  if (type === 'text/csv') return 'fa-solid fa-file-csv'
  if (type === 'application/json' || type === 'application/xml' || type === 'text/xml') return 'fa-solid fa-file-code'
  if (type.startsWith('text/')) return 'fa-solid fa-file-lines'
  if (type.startsWith('audio/')) return 'fa-solid fa-file-audio'
  if (type.startsWith('video/')) return 'fa-solid fa-file-video'
  if (WORD_TYPES.has(type)) return 'fa-solid fa-file-word'
  if (EXCEL_TYPES.has(type)) return 'fa-solid fa-file-excel'
  if (PPT_TYPES.has(type)) return 'fa-solid fa-file-powerpoint'
  if (ARCHIVE_TYPES.has(type)) return 'fa-solid fa-file-zipper'
  return 'fa-solid fa-file'
}

// Aucun événement n'est émis par ce composant (pas d'appel à onTbsEventCallback, contrairement aux
// autres types) — spécification explicite, pas un oubli.
export default function MultiFileField({ id, label, value, fixedHeight, visible, deleteMessage, fileExt, compoGroup, userData, class: cssClass, style: rawStyle }) {
  const cssStyle = toStyleObject(rawStyle)
  // `visible: false` (JSON) → display:none posé directement sur le div [data-tbs-root] : voir
  // ChampField.jsx pour le détail (show()/hide() de tbsGeneralApi.js ciblent ce même élément).
  const visibilityStyle = visible === false ? { display: 'none' } : undefined

  // `fileExt` — chaîne d'extensions séparées par `;` (ex. "txt;xlsx;docx;png"). Vide/absente → tous
  // types acceptés. Normalisée en minuscules, sans le point (comparaison sur l'extension du nom de
  // fichier, insensible à la casse).
  const allowedExts = useMemo(() => {
    const parsed = (fileExt ?? '').split(';').map(e => e.trim().toLowerCase()).filter(Boolean)
    return parsed.length ? parsed : null
  }, [fileExt])
  const isExtAllowed = useCallback(name => {
    if (!allowedExts) return true
    const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : ''
    return allowedExts.includes(ext)
  }, [allowedExts])
  // Forme d'un fichier de la liste : { key, name, file, size, type, contentUrl, deleteUrl }.
  // `key` : identifiant généré par l'appli (jamais par le JSON/le code métier). `file` : objet
  // File réel (ajout local, bouton/glisser-déposer) ou `null` (fichier déjà côté serveur, contenu
  // binaire non disponible côté navigateur — `contentUrl`/`deleteUrl` pilotent alors visualisation/
  // téléchargement/suppression). `value` (JSON initial) suit ce même format, `file` toujours `null`
  // (JSON ne peut pas porter un objet File).
  const [files, setFiles]     = useState(() =>
    (value ?? []).map(f => ({
      key:        f.key ?? generateId(),
      name:       f.name,
      size:       f.size ?? 0,
      type:       inferMimeType(f.name, f.type),
      contentUrl: f.contentUrl ?? null,
      deleteUrl:  f.deleteUrl ?? null,
      file:       null,
    }))
  )
  const [preview, setPreview] = useState(null)
  const inputRef      = useRef(null)
  const previewUrl    = useRef(null)
  // Incrémenté à chaque handleView() — un fetch(contentUrl) encore en vol pour un fichier texte
  // ouvert précédemment ne doit jamais écraser l'aperçu affiché entre-temps pour un autre fichier.
  const previewSeqRef = useRef(0)
  const compoGroupRef = useRef(compoGroup ?? null)
  const userDataRef   = useRef(userData ?? null)

  const filesRef = useRef(files)
  useLayoutEffect(() => { filesRef.current = files })

  // getFiles() lit filesRef.current : sans assignation synchrone à chaque écriture, un appel juste
  // après un ajout/suppression lirait encore l'ancienne liste (la ref n'est resynchronisée qu'au
  // rendu suivant via le useLayoutEffect ci-dessus).
  const appendFile = useCallback(fileWrapper => {
    const next = [...filesRef.current, fileWrapper]
    filesRef.current = next
    setFiles(next)
  }, [])

  // `addFile(fileWrapper)` — ajoute un fichier déjà entièrement formé (typiquement un fichier côté
  // serveur, avec `contentUrl`/`deleteUrl` déjà connus) à la liste existante. Ne passe pas par le
  // flux d'upload (`onTbsAddFileUpload`) ni par le filtre `fileExt` — ceux-ci ne concernent que la
  // saisie utilisateur (bouton/glisser-déposer, voir `addLocalFiles` ci-dessous).
  const addFile = useCallback(fileWrapper => {
    appendFile({
      key:        fileWrapper.key ?? generateId(),
      name:       fileWrapper.name,
      size:       fileWrapper.size ?? 0,
      type:       inferMimeType(fileWrapper.name, fileWrapper.type),
      contentUrl: fileWrapper.contentUrl ?? null,
      deleteUrl:  fileWrapper.deleteUrl ?? null,
      file:       fileWrapper.file ?? null,
    })
  }, [appendFile])

  const [isDragOver, setIsDragOver] = useState(false)
  const dragCounter = useRef(0)

  // `fileExt` : le filtre `accept` de l'<input type="file"> ne s'applique qu'à la boîte de dialogue
  // native (browse) — pas au glisser-déposer, qui accepte n'importe quel fichier de l'OS. Revalidé
  // ici pour les deux voies d'ajout.
  //
  // Chaque fichier local suit le flux décrit par le code métier : (1) fileWrapper créé avec
  // name/size/file, mais key/contentUrl/deleteUrl à null ; (2) window.onTbsAddFileUpload(compoId,
  // fileWrapper) — le code métier y uploade le contenu et met à jour key/contentUrl/deleteUrl sur
  // l'objet (mutation directe), puis renvoie true/false (ou une Promise<boolean>, cf. le même
  // contrat que onTbsGridLoader/onTbsTreeLoader/tbsDynaComboLoader — exception ou rejet de promesse →
  // console.error, traité comme false, sans crash) ; (3) false → fichier non ajouté à la liste,
  // true → inséré avec le fileWrapper (déjà mis à jour par le code métier).
  // Fichiers traités séquentiellement (pas Promise.all) : évite les écritures concurrentes sur
  // filesRef.current lors d'un ajout multiple (sélection/glisser-déposer de plusieurs fichiers).
  const addLocalFiles = useCallback(async fileList => {
    const incoming = Array.from(fileList)
    const rejected = incoming.filter(f => !isExtAllowed(f.name))
    if (rejected.length) {
      window.tbsUserMessages?.toast({
        severity: 'error',
        message: `Extension non autorisée : ${rejected.map(f => f.name).join(', ')}`,
        duration: 5000,
      })
    }
    for (const f of incoming.filter(f => isExtAllowed(f.name))) {
      const fileWrapper = { key: null, name: f.name, size: f.size, type: inferMimeType(f.name, f.type), contentUrl: null, deleteUrl: null, file: f }
      let ok
      try {
        ok = await window.onTbsAddFileUpload?.(id, fileWrapper)
      } catch (e) {
        console.error(e)
        ok = false
      }
      if (!ok) continue
      fileWrapper.key = fileWrapper.key ?? generateId()
      appendFile(fileWrapper)
    }
  }, [id, isExtAllowed, appendFile])

  const handleAdd = useCallback(e => {
    addLocalFiles(e.target.files)
    e.target.value = ''
  }, [addLocalFiles])

  // dragCounter compte les entrées/sorties imbriquées (enfants du drop zone) : dragleave se
  // déclenche aussi en survolant un enfant, un simple booléen clignoterait sinon.
  const handleDragEnter = useCallback(e => {
    e.preventDefault()
    dragCounter.current += 1
    if (e.dataTransfer.types.includes('Files')) setIsDragOver(true)
  }, [])

  const handleDragOver = useCallback(e => {
    e.preventDefault()
    if (e.dataTransfer.types.includes('Files')) e.dataTransfer.dropEffect = 'copy'
  }, [])

  const handleDragLeave = useCallback(e => {
    e.preventDefault()
    dragCounter.current -= 1
    if (dragCounter.current <= 0) { dragCounter.current = 0; setIsDragOver(false) }
  }, [])

  const handleDrop = useCallback(e => {
    e.preventDefault()
    dragCounter.current = 0
    setIsDragOver(false)
    addLocalFiles(e.dataTransfer.files)
  }, [addLocalFiles])

  const removeLocally = useCallback(key => {
    const next = filesRef.current.filter(f => f.key !== key)
    filesRef.current = next
    setFiles(next)
  }, [])

  // Suppression effective : sans `deleteUrl` (fichier jamais confirmé côté serveur), retrait local
  // direct. Avec `deleteUrl`, appel POST — réponse JSON `{ status: 'OK'|'KO', message? }` : `KO` →
  // alerte avec `message`, fichier conservé ; `OK` → retrait local. Toute erreur réseau/parsing est
  // traitée comme un échec (alerte générique), jamais un crash.
  const doDelete = useCallback(async key => {
    const item = filesRef.current.find(f => f.key === key)
    if (!item) return
    if (!item.deleteUrl) { removeLocally(key); return }
    try {
      const resp = await fetch(item.deleteUrl, { method: 'POST' })
      const json = await resp.json()
      if (json.status === 'KO') {
        window.tbsUserMessages?.messageBox({ severity: 'error', title: 'Erreur', message: json.message || 'Suppression impossible.' })
        return
      }
      removeLocally(key)
    } catch (e) {
      console.error(e)
      window.tbsUserMessages?.messageBox({ severity: 'error', title: 'Erreur', message: 'La suppression a échoué.' })
    }
  }, [removeLocally])

  // `removeAllFiles()` — même flux que `doDelete()` (deleteUrl si présent, sinon retrait local),
  // appliqué en parallèle à chaque fichier de la liste courante. Chaque `doDelete(key)` relit
  // `filesRef.current` au moment de son propre retrait, donc reste correct même si une suppression
  // échoue (KO) pendant que les autres se terminent.
  const removeAllFiles = useCallback(() => Promise.all(filesRef.current.map(f => doDelete(f.key))), [doDelete])

  // `deleteMessage` absent → suppression immédiate (comportement historique). Présent → confirmation
  // via `window.tbsUserMessages.confirmationBox()` avant suppression effective. Ne s'applique qu'à
  // la suppression déclenchée depuis l'UI (bouton corbeille) — `removeFile()`/`removeAllFiles()`
  // (API impérative) suppriment directement, sans confirmation.
  const handleDelete = useCallback(key => {
    if (!deleteMessage) { doDelete(key); return }
    if (!window.tbsUserMessages) { doDelete(key); return }
    window.tbsUserMessages.confirmationBox({
      title: 'Confirmation',
      message: deleteMessage,
      callbackFn: confirmed => { if (confirmed) doDelete(key) },
    })
  }, [deleteMessage, doDelete])

  // Fichier local (`item.file`, objet File réel) → URL blob temporaire, révoquée juste après.
  // Fichier serveur (`item.contentUrl`, pas de contenu binaire côté navigateur) → lien direct, rien
  // à révoquer (jamais créé via createObjectURL).
  const handleDownload = useCallback(item => {
    const url = item.file ? URL.createObjectURL(item.file) : item.contentUrl
    if (!url) return
    const a   = document.createElement('a')
    a.href     = url
    a.download = item.name
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    if (item.file) URL.revokeObjectURL(url)
  }, [])

  // Règle commune à toute prévisualisation (image/PDF/texte) : `item.file` (objet File réel) est
  // toujours prioritaire s'il est présent ; sinon, le contenu est récupéré via `item.contentUrl`.
  // Pour le texte, le contenu doit être lu explicitement (`file.text()` ou `fetch(contentUrl).text()`)
  // pour être injecté dans un <pre>. Pour le PDF sans `file` (fichier serveur), le contenu est aussi
  // récupéré explicitement (`fetch(contentUrl).blob()` → `URL.createObjectURL()`) plutôt que posé
  // directement en `src=` de l'<iframe> : un `src=` distant peut être bloqué par le serveur cible
  // (en-tête `X-Frame-Options`/CSP `frame-ancestors` — message navigateur « Ce contenu est bloqué »),
  // alors qu'une URL `blob:` locale n'est jamais soumise à ces restrictions (contrainte propre à
  // l'affichage en cadre, pas au chargement de l'image — <img> n'y est pas exposé, d'où l'absence de
  // ce traitement pour les images). Si le fetch échoue lui-même (CORS non ouvert côté serveur, etc.),
  // dégrade proprement en message d'erreur — un vrai contournement nécessiterait un proxy serveur.
  const handleView = useCallback(async item => {
    if (previewUrl.current) { URL.revokeObjectURL(previewUrl.current); previewUrl.current = null }
    const seq = ++previewSeqRef.current
    if (isTextType(item.type)) {
      if (item.file) {
        const text = await item.file.text()
        if (previewSeqRef.current === seq) setPreview({ name: item.name, type: item.type, text })
        return
      }
      if (!item.contentUrl) return
      setPreview({ name: item.name, type: item.type, loading: true })
      try {
        const resp = await fetch(item.contentUrl)
        const text = await resp.text()
        if (previewSeqRef.current === seq) setPreview({ name: item.name, type: item.type, text })
      } catch (e) {
        console.error(e)
        if (previewSeqRef.current === seq) setPreview({ name: item.name, type: item.type, error: true })
      }
      return
    }
    if (item.type === 'application/pdf' && !item.file) {
      if (!item.contentUrl) return
      setPreview({ name: item.name, type: item.type, loading: true })
      try {
        const resp = await fetch(item.contentUrl)
        const blob = await resp.blob()
        const url  = URL.createObjectURL(blob)
        if (previewSeqRef.current !== seq) { URL.revokeObjectURL(url); return }
        previewUrl.current = url
        setPreview({ name: item.name, type: item.type, url })
      } catch (e) {
        console.error(e)
        if (previewSeqRef.current === seq) setPreview({ name: item.name, type: item.type, error: true })
      }
      return
    }
    const url = item.file ? (previewUrl.current = URL.createObjectURL(item.file)) : item.contentUrl
    if (!url) return
    setPreview({ name: item.name, type: item.type, url })
  }, [])

  const closePreview = useCallback(() => {
    if (previewUrl.current) { URL.revokeObjectURL(previewUrl.current); previewUrl.current = null }
    previewSeqRef.current++
    setPreview(null)
  }, [])

  useEffect(() => {
    window.tbsComponents = window.tbsComponents ?? {}
    window.tbsComponents[id] = {
      type: 'multifile',
      ...createGeneralApi(id, userDataRef.current),
      getFiles:      () => filesRef.current.map(f => ({ ...f })),
      addFile:       fileWrapper => addFile(fileWrapper),
      removeFile:    fileKey => doDelete(fileKey),
      removeAllFiles: () => removeAllFiles(),
      getCompoGroup: () => compoGroupRef.current,
      setCompoGroup: v => { compoGroupRef.current = v },
    }
    return () => { delete window.tbsComponents[id] }
  }, [id, addFile, doDelete, removeAllFiles])

  return (
    <div
      id={id}
      data-tbs-root={id}
      className={cn('mb-2 flex flex-col gap-2 max-w-2xl rounded-md', isDragOver && 'ring-2 ring-[hsl(var(--primary))] ring-offset-2', cssClass)}
      style={{ ...cssStyle, ...visibilityStyle }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {label && <Label>{label}</Label>}

      <input ref={inputRef} type="file" multiple className="hidden" onChange={handleAdd}
        accept={allowedExts ? allowedExts.map(e => `.${e}`).join(',') : undefined} />

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="self-start" onClick={() => inputRef.current?.click()}>
          <Plus size={14} className="mr-1" /> Ajouter un document
        </Button>
        <span className="text-xs text-[hsl(var(--muted-foreground))]">ou glisser-déposer un fichier ici</span>
      </div>

      {files.length > 0 && (
        <ul className="flex flex-col gap-1" style={fixedHeight ? { height: toCssLength(fixedHeight), overflowY: 'auto' } : undefined}>
          {files.map(item => (
            <li key={item.key} className="flex items-center gap-3 px-3 py-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm">
              <span className="text-[hsl(var(--muted-foreground))] shrink-0">
                <i className={iconClassFor(item.type)} aria-hidden="true" />
              </span>
              <span className="flex-1 truncate">{item.name}</span>
              <span className="text-xs text-[hsl(var(--muted-foreground))] shrink-0">{formatSize(item.size)}</span>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Visualiser"   onClick={() => handleView(item)}>
                  <Eye size={13} />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Télécharger"  onClick={() => handleDownload(item)}>
                  <Download size={13} />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-[hsl(var(--destructive))]" title="Supprimer" onClick={() => handleDelete(item.key)}>
                  <Trash2 size={13} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {preview && createPortal(
        <DraggableShell title={preview.name} onClose={closePreview} initW={760} initH={520} bodyStyle={{ padding: 0 }}>
          <div className="w-full h-full flex justify-center items-center overflow-auto">
            {preview.loading
              ? <p className="text-[hsl(var(--muted-foreground))] text-sm italic">Chargement…</p>
              : preview.error
                ? <p className="text-[hsl(var(--destructive))] text-sm">Impossible de charger le contenu du fichier.</p>
                : preview.type.startsWith('image/')
                  ? <img src={preview.url} alt={preview.name} className="max-w-full max-h-full object-contain" />
                  : preview.type === 'application/pdf'
                    ? <iframe src={preview.url} title={preview.name} className="w-full h-full border-0" />
                    : isTextType(preview.type)
                      ? <pre className="w-full h-full overflow-auto text-xs font-mono whitespace-pre-wrap break-words text-left bg-[hsl(var(--muted))] p-3 m-0">{preview.text}</pre>
                      : <p className="text-[hsl(var(--muted-foreground))] text-sm italic">Prévisualisation non disponible pour ce type de fichier.</p>
            }
          </div>
        </DraggableShell>,
        document.body
      )}
    </div>
  )
}
